import React from 'react';
import { 
  Box, Typography, Paper, Chip, Button, 
  Divider, Grid, Avatar, Stack, Alert 
} from '@mui/material';
import { 
  ArrowBack, Edit, Check, Close, 
  Schedule, CalendarToday, DoneAll 
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { tickets, users } from '../utils/dummyData';

const TicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Find the ticket by ID
  const ticket = tickets.find(t => t.id === parseInt(id));
  
  // If ticket not found
  if (!ticket) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Ticket not found!</Alert>
        <Button 
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
          color="error"
        >
          Back to Tickets
        </Button>
      </Box>
    );
  }

  // Find assignee details
  const assignee = users.find(u => u.id === ticket.assignee);
  
  // Status chip styling
  const statusChip = {
    'Pending Approval': { color: 'warning', icon: <Schedule /> },
    'In Progress': { color: 'info', icon: <CalendarToday /> },
    'Completed': { color: 'success', icon: <DoneAll /> },
    'Rejected': { color: 'error', icon: <Close /> }
  };

  // Priority chip styling
  const priorityChip = {
    'Low': { color: 'success' },
    'Medium': { color: 'warning' },
    'High': { color: 'error' },
    'Critical': { color: 'error' }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        color="error"
        sx={{ mb: 2 }}
      >
        Back to Tickets
      </Button>

      <Paper sx={{ p: 3, borderLeft: '4px solid #d32f2f' }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3
        }}>
          <Typography variant="h4" color="#d32f2f">
            {ticket.title}
          </Typography>
          <Chip
            icon={statusChip[ticket.status]?.icon}
            label={ticket.status}
            color={statusChip[ticket.status]?.color || 'default'}
            variant="outlined"
            sx={{ fontWeight: 'bold' }}
          />
        </Box>

        <Grid container spacing={3}>
          {/* Left Column - Ticket Details */}
          <Grid item xs={12} md={8}>
            <Typography variant="h6" gutterBottom color="#d32f2f">
              Description
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography>{ticket.description}</Typography>
            </Paper>

            <Typography variant="h6" gutterBottom color="#d32f2f">
              Timeline
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2">Expected</Typography>
                  <Typography>
                    {ticket.expectedDateStart} to {ticket.expectedDateEnd}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Lead Time: {ticket.leadTime}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2">Actual</Typography>
                  {ticket.actualDateStart ? (
                    <>
                      <Typography>
                        {ticket.actualDateStart} to {ticket.actualDateEnd || 'Present'}
                      </Typography>
                      {ticket.resolvedDate && (
                        <Typography variant="caption" color="text.secondary">
                          Resolved on: {ticket.resolvedDate}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography color="text.secondary">Not started</Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* Right Column - Metadata */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom color="#d32f2f">
              Ticket Information
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2">Priority</Typography>
                  <Chip
                    label={ticket.priority}
                    color={priorityChip[ticket.priority]?.color || 'default'}
                    size="small"
                  />
                </Box>
                
                <Box>
                  <Typography variant="subtitle2">Assignee</Typography>
                  {assignee ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          mr: 1,
                          bgcolor: assignee.department === 'Sales' ? '#d32f2f' : '#0288d1'
                        }}
                      >
                        {assignee.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography>{assignee.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {assignee.department} • {assignee.role}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Typography color="text.secondary">Unassigned</Typography>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2">Dates</Typography>
                  <Typography variant="caption" display="block">
                    Created: {ticket.createdAt}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Last Updated: {ticket.updatedAt}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {ticket.status !== 'Completed' && ticket.status !== 'Rejected' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  color="error"
                  fullWidth
                >
                  Edit Ticket
                </Button>
                {ticket.status === 'Pending Approval' && (
                  <>
                    <Button
                      variant="contained"
                      startIcon={<Check />}
                      color="success"
                      fullWidth
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Close />}
                      color="error"
                      fullWidth
                    >
                      Reject
                    </Button>
                  </>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default TicketDetails;